import React, { useEffect, useState } from 'react'
import { Box, IconButton } from '@mui/material'
import { Button, Paper } from '@material-ui/core'
import { Badge as BadgeIcon, Delete, Edit, Settings } from '@material-ui/icons'
import { KeyboardArrowRight } from '@mui/icons-material'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { toast } from 'utils/notify'
import { first } from 'lodash'
import { jsPDF } from 'jspdf'
import QRCode from 'qrcode'
import { badgesSubjectsApplication, Collection } from 'constants/index'
import {
  getBadgeSubjectsData,
  deleteBadgeSubject,
  bulkDeleteBadgeSubjects,
  applicableBadgeConfiguration
} from 'store/badges'
import { renderCol } from 'utils/renderColumns'
import getTableOptions from 'utils/getTableOptions'
import CustomTabs from 'components/CustomTabs'
import BasicTable from 'components/BasicTable'
import ResourceNotFound from 'components/ResourceNotFound'
import ConfirmModal from 'components/ConfirmationModal'
import ViewHeader from 'components/ViewHeader'
import DataLoading from 'components/DataLoading'
import AddEditBadgeSubjectModal from './AddEditBadgeSubject/AddEditBadgeSubjectModal'
import ImportBadgesCSVModal from 'views/admin/BadgesV1/ImportBadgesCSVModal'
import PrintBadgesModal from 'views/admin/BadgesV1/PrintBadgesModal'
import { downloadBadgesInCSVV1 } from 'utils/downloadCSV'
import {
  getBadgeDimensions,
  adjustFontSizeToFitWidth,
  truncateTextToWidth,
  buildBadgeQrContent
} from 'utils/badges'
import { getContrastColor, componentToHex } from 'utils/colors'
import { formatToFourDigits, loadImageBase64 } from 'utils'
import { storageRef } from '../../../index'
import { useIndexStyles } from './styles'

// Helvetica's cap height as a fraction of its font size. Lets a baseline be derived from where
// the top of the capitals should land, which is what the eye actually reads as "text position".
const HELVETICA_CAP_HEIGHT_RATIO = 0.717

const BadgesV1Index = () => {
  const classes = useIndexStyles()
  const history = useHistory()
  const { url } = useRouteMatch()
  const { conference } = useSelector((state) => state.conference)

  // List state
  const [subjects, setSubjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [typeFilter, setTypeFilter] = useState(badgesSubjectsApplication[0])

  // Add/Edit modal
  const [formModalOpen, setFormModalOpen] = useState(false)
  const [editingSubjectId, setEditingSubjectId] = useState(null)

  // Delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deletingSubjectId, setDeletingSubjectId] = useState(null)
  const [deleting, setDeleting] = useState(false)

  // CSV import modal
  const [csvModalOpen, setCsvModalOpen] = useState(false)

  // Bulk delete
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)

  // Row selection
  const [selectedRows, setSelectedRows] = useState([])

  // Pagination state — must be in state to survive re-renders on checkbox selection
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [page, setPage] = useState(0)

  // PDF print modal
  const [pdfConfigModal, setPdfConfigModal] = useState(false)
  const [subjectsToBePrinted, setSubjectsToBePrinted] = useState([])
  const [printingBadges, setPrintingBadges] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)

  const handleRowSelectionChange = (_, allRowsSelected) => {
    setSelectedRows(allRowsSelected.map((row) => row.dataIndex))
  }

  // Real-time listener
  useEffect(() => {
    if (!conference?.id) return
    setLoading(true)
    const unsubscribe = getBadgeSubjectsData(conference.id, (data) => {
      setSubjects(data)
      setLoading(false)
    })
    return () => unsubscribe && unsubscribe()
  }, [conference?.id])

  const filteredSubjects = subjects.filter((s) => s.type === typeFilter)

  // Add/Edit handlers
  const openAddModal = () => {
    setEditingSubjectId(null)
    setFormModalOpen(true)
  }
  const openEditModal = (id) => {
    setEditingSubjectId(id)
    setFormModalOpen(true)
  }
  const closeFormModal = () => {
    setFormModalOpen(false)
    setEditingSubjectId(null)
  }

  // Delete handlers
  const openDeleteModal = (id) => {
    setDeletingSubjectId(id)
    setDeleteModalOpen(true)
  }
  const closeDeleteModal = () => {
    setDeleteModalOpen(false)
    setDeletingSubjectId(null)
  }
  const handleDelete = async () => {
    setDeleting(true)
    await deleteBadgeSubject(deletingSubjectId)
    setDeleting(false)
    closeDeleteModal()
  }

  // Bulk delete handlers
  const handleBulkDelete = async () => {
    setBulkDeleting(true)
    const ids = selectedRows.map((dataIndex) => filteredSubjects[dataIndex]?.id).filter(Boolean)
    await bulkDeleteBadgeSubjects(ids)
    setSelectedRows([])
    setBulkDeleting(false)
    setBulkDeleteModalOpen(false)
  }

  // PDF print handlers
  const openPdfConfigModal = (subjects) => {
    setSubjectsToBePrinted(subjects)
    setPdfConfigModal(true)
  }
  const closePdfConfigModal = () => {
    setSubjectsToBePrinted([])
    setPdfConfigModal(false)
  }

  const handlePrintSubmit = async ({ pageSize, orientation, bannerLabel }) => {
    setIsPrinting(true)
    try {
      await downloadBadgesInPdfFile(
        subjectsToBePrinted,
        first(subjectsToBePrinted).type,
        pageSize,
        orientation,
        'print',
        bannerLabel
      )
    } catch {
      toast.error('PDF generation failed. Try again.')
    } finally {
      setIsPrinting(false)
      closePdfConfigModal()
    }
  }

  const handleDownloadSubmit = async ({ pageSize, orientation, bannerLabel }) => {
    setPrintingBadges(true)
    try {
      await downloadBadgesInPdfFile(
        subjectsToBePrinted,
        first(subjectsToBePrinted).type,
        pageSize,
        orientation,
        'download',
        bannerLabel
      )
    } catch {
      toast.error('Could not generate PDF. Try again.')
    } finally {
      setPrintingBadges(false)
      closePdfConfigModal()
    }
  }

  const downloadBadgesInPdfFile = async (
    subjects,
    type,
    pageSize,
    orientation,
    submissionType,
    bannerLabel
  ) => {
    try {
      const {
        isLandscape,
        startX,
        startY,
        logoWidth,
        logoHeight,
        qrHeight,
        qrWidth,
        badgeWidth,
        badgeHeight,
        spacingX,
        spacingY,
        badgePerRow,
        badgePerColumn
      } = getBadgeDimensions(orientation, pageSize)

      const applicableConfig = await applicableBadgeConfiguration(type, conference?.id)
      if (!applicableConfig) {
        toast.warning(`No print configuration found for ${type?.toLowerCase()} subject.`)
        return
      }

      // Deserialize badgeLayout — Firestore may store arrays as {"0":[...],"1":[...]} objects
      const rawLayout = applicableConfig?.badgeLayout
      const badgeLayout = Array.isArray(rawLayout)
        ? rawLayout
        : rawLayout && typeof rawLayout === 'object'
        ? Array.from({ length: 5 }, (_, i) => rawLayout[String(i)] || [])
        : []

      const badgeNameFormat = applicableConfig?.badgeNameFormat || 'firstName lastName'
      const badgeLineFontSizes = applicableConfig?.badgeLineFontSizes || [22, 12, 12, 12, 12]
      const badgeLabelFontSize = applicableConfig?.badgeLabelFontSize ?? 30
      const badgeContentAlignment = applicableConfig?.badgeContentAlignment || 'center'

      const { id, confLogo } = conference
      let logoUrl = null
      try {
        logoUrl = await storageRef
          .child(`${id}/${Collection.CONFERENCES}/${confLogo}`)
          .getDownloadURL()
      } catch {
        toast.warning('No badge banner found. Upload one before printing.')
        return
      }
      const logoBase64 = await loadImageBase64(logoUrl)

      const fieldsToEncodeInQRCode = applicableConfig?.fieldsAppliedInsideQRCode || []
      const badgeNameFormatQR = applicableConfig?.badgeNameFormat || 'firstName lastName'

      const qrCodePromises = subjects?.map((subject) => {
        const dataToEncodeInQRCode = {}
        fieldsToEncodeInQRCode.forEach((field) => {
          dataToEncodeInQRCode[field] = subject[field]
        })

        const resultToBeEncoded = buildBadgeQrContent(
          dataToEncodeInQRCode,
          fieldsToEncodeInQRCode,
          badgeNameFormatQR,
          conference?.badgeQrCodeFormat
        )

        return QRCode.toDataURL(resultToBeEncoded ? resultToBeEncoded : ' ', {
          margin: 1,
          width: 400,
          errorCorrectionLevel: 'L'
        })
      })
      const qrCodes = await Promise.all(qrCodePromises)

      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: pageSize,
        compress: true
      })

      const drawBadge = (data, qrCode, x, y) => {
        doc.addImage(logoBase64, 'PNG', x + 0.075, y + 0.075, logoWidth, logoHeight)

        const isLeftAligned = badgeContentAlignment === 'left'
        const textX = isLeftAligned ? x + 0.075 : x + badgeWidth / 2
        const textOptions = { align: isLeftAligned ? 'left' : 'center' }
        const availableWidth = badgeWidth - 0.175
        const qrCodeX = isLandscape
          ? x + badgeWidth - qrWidth - 0.075
          : x + badgeWidth / 2 - qrWidth / 2

        // ── Line 1: name only (no credential), driven by badgeNameFormat ──
        const rawFirst = (data?.firstName || '').toUpperCase()
        const rawLast = (data?.lastName || '').toUpperCase()
        const line1Text =
          badgeNameFormat === 'lastName, firstName'
            ? rawLast
              ? `${rawLast}, ${rawFirst}`
              : rawFirst
            : rawLast
            ? `${rawFirst} ${rawLast}`
            : rawFirst

        // The name sits above the QR row (unlike lines 2–5, which sit beside it in
        // landscape), so it can use the full available width, not the QR-clamped one.
        doc.setTextColor(0, 0, 0).setFont('helvetica', 'bold')
        const name1BaseFontSize = badgeLineFontSizes[0]
        let name1FontSize = adjustFontSizeToFitWidth(
          doc,
          line1Text,
          name1BaseFontSize,
          availableWidth
        )
        let name1Wrapped = doc.splitTextToSize(line1Text, availableWidth)
        // Same near-miss correction as lines 2–5 below: nudge down a little further to
        // try collapsing back to one line before accepting a genuine wrap.
        let name1ExtraSteps = 0
        while (name1Wrapped.length > 1 && name1FontSize > 4 && name1ExtraSteps < 3) {
          name1FontSize -= 0.5
          doc.setFontSize(name1FontSize)
          name1Wrapped = doc.splitTextToSize(line1Text, availableWidth)
          name1ExtraSteps += 1
        }
        // Guards the case splitTextToSize can't wrap: a single token (no space) still
        // wider than availableWidth even at the font-size floor.
        name1Wrapped = name1Wrapped.map((line) => truncateTextToWidth(doc, line, availableWidth))

        // A glyph's cap height scales with its font size, so a fixed baseline offset makes the
        // visible top of the name drift with the resolved size — upward far enough to overlap the
        // banner once a long name shrinks several points below its configured size. Pin the top of
        // the capitals a constant gap under the banner instead and derive the baseline from there,
        // so the name sits directly below the banner whether it rendered at its configured 28pt or
        // bottomed out at the 4pt floor. Half the gap the default 22pt name used to have — the old
        // 0.126" read as too loose once the name stopped drifting upward into the banner.
        const NAME_TOP_GAP_IN = 0.063
        const logoBottomY = y + 0.075 + logoHeight
        const LINE1_BASELINE =
          logoBottomY + NAME_TOP_GAP_IN + (name1FontSize / 72) * HELVETICA_CAP_HEIGHT_RATIO

        // ── Lines 2–5: resolve font size + wrapping up front (no drawing yet) so the
        // total text height is known before we decide how tightly to space it. ──
        const bottomBarY = y + badgeHeight - 0.51
        const bodyLines = badgeLayout.slice(1).reduce((acc, fieldKeys, idx) => {
          if (!fieldKeys || fieldKeys.length === 0) return acc

          const parts = []
          for (const key of fieldKeys) {
            if (data?.[key]) parts.push(data[key])
          }
          const lineText = parts.filter(Boolean).join(' · ')
          if (!lineText) return acc

          const lineIndex = idx + 1
          const baseFontSize = badgeLineFontSizes[lineIndex] ?? 12
          doc.setFont('helvetica', 'normal')
          const lineWidth = isLandscape
            ? isLeftAligned
              ? qrCodeX - textX - 0.1
              : (badgeWidth - qrWidth - 0.15) * 2 - badgeWidth
            : availableWidth
          let resolvedFontSize = adjustFontSizeToFitWidth(doc, lineText, baseFontSize, lineWidth)
          let wrapped = doc.splitTextToSize(lineText, lineWidth)
          // getTextWidth and splitTextToSize measure slightly differently in jsPDF — a couple
          // of extra 0.5pt steps corrects near-miss wraps without collapsing genuinely long text.
          let extraSteps = 0
          while (wrapped.length > 1 && resolvedFontSize > 4 && extraSteps < 3) {
            resolvedFontSize -= 0.5
            doc.setFontSize(resolvedFontSize)
            wrapped = doc.splitTextToSize(lineText, lineWidth)
            extraSteps += 1
          }
          // Same last-resort guard as the name line above.
          wrapped = wrapped.map((line) => truncateTextToWidth(doc, line, lineWidth))
          return [...acc, { lineWidth, resolvedFontSize, wrapped }]
        }, [])

        // Font sizes are user-configured (up to 28pt for the name, 16pt for body lines), so
        // instead of overriding them when a combination doesn't fit, tighten the shared
        // line-height (ideally 1.3) down to a single-spaced floor of 1.0. The card's geometry
        // is fixed, so the room available for text can be computed up front rather than
        // measured after drawing.
        const IDEAL_LINE_MULT = 1.3
        // 1.0 = single-spaced, matching both the comment above and the preview's MIN_LINE_HEIGHT.
        // The floor only binds once 1.3 already fails to fit, so tightening it can't affect a
        // badge that fits — it only buys room for the ones that would otherwise overrun the QR.
        const MIN_LINE_MULT = 1.0
        const SAFETY_MARGIN_IN = 0.03
        // Lines 2–5 sit beside the QR in landscape, not stacked above it, so only portrait's
        // text budget needs to reserve room for the QR + counter block below it. The counter
        // is a short digit string with no descenders, so its own height doesn't need reserving
        // below the baseline — the existing 0.135 gap already covers that.
        const qrReserveIn = isLandscape ? 0 : qrHeight + 0.135
        const availableForTextIn =
          bottomBarY - LINE1_BASELINE - 0.24 - qrReserveIn - SAFETY_MARGIN_IN
        // Only extra *wrapped* name lines scale with the multiplier below — the base name
        // line's height is already absorbed into the fixed LINE1_BASELINE/0.24 offsets.
        const rawTextHeightIn =
          (name1Wrapped.length - 1) * (name1FontSize / 72) +
          bodyLines.reduce((sum, l) => sum + l.wrapped.length * (l.resolvedFontSize / 72), 0)
        const lineMult =
          rawTextHeightIn > 0 && rawTextHeightIn * IDEAL_LINE_MULT > availableForTextIn
            ? Math.max(MIN_LINE_MULT, availableForTextIn / rawTextHeightIn)
            : IDEAL_LINE_MULT

        doc.setFont('helvetica', 'bold').setFontSize(name1FontSize)
        doc.text(name1Wrapped, textX, LINE1_BASELINE, textOptions)

        // No shrink-dependent nudge here: LINE1_BASELINE above is now geometrically correct for
        // whatever size the name resolved to, so the body lines are a constant offset below it.
        const name1LineHeight = (name1FontSize / 72) * lineMult
        let textY = LINE1_BASELINE + 0.24 + (name1Wrapped.length - 1) * name1LineHeight

        doc.setFont('helvetica', 'normal')
        bodyLines.forEach(({ resolvedFontSize, wrapped }) => {
          doc.setFontSize(resolvedFontSize)
          doc.text(wrapped, textX, textY, textOptions)
          textY += wrapped.length * ((resolvedFontSize / 72) * lineMult)
        })

        // ── QR code + counter ──
        // Hard guarantee regardless of the above: the QR + counter block (portrait only —
        // landscape's QR is a fixed offset beside the text, not stacked below it) never
        // starts low enough to land under the footer bar.
        const qrCodeY = isLandscape
          ? y + logoHeight + 0.645
          : Math.min(textY + 0.02, bottomBarY - qrHeight - 0.135 - SAFETY_MARGIN_IN)

        if (data?.counter && isLandscape) {
          doc.setFontSize(10).setFont('helvetica', 'bold')
          doc.text(formatToFourDigits(data.counter), qrCodeX + qrWidth / 2, qrCodeY - 0.03, {
            align: 'center'
          })
        }

        doc.addImage(qrCode, 'PNG', qrCodeX, qrCodeY, qrWidth, qrHeight)

        if (data?.counter && !isLandscape) {
          doc.setFontSize(10).setFont('helvetica', 'bold')
          doc.text(
            formatToFourDigits(data.counter),
            qrCodeX + qrWidth / 2,
            qrCodeY + qrHeight + 0.135,
            { align: 'center' }
          )
        }

        // ── Footer bar ──
        const fillColor = applicableConfig?.badgeBarColor ?? '#000000'
        const barTextColor = getContrastColor(fillColor)
        const resolvedFillColor = fillColor?.startsWith('rgba')
          ? (() => {
              const [r, g, b, a] = fillColor.match(/[\d.]+/g).map(Number)
              const blend = (c) => Math.round((1 - a) * 255 + a * c)
              return `#${componentToHex(blend(r))}${componentToHex(blend(g))}${componentToHex(
                blend(b)
              )}`
            })()
          : fillColor
        doc
          .setFillColor(resolvedFillColor)
          .rect(x + 0.075, bottomBarY, badgeWidth - 0.15, 0.47, 'F')

        const roleText = (bannerLabel?.trim() || type).toUpperCase()
        doc.setTextColor(barTextColor).setFont('helvetica', 'bold')
        const labelWidth = badgeWidth - 0.3
        const resolvedLabelFontSize = adjustFontSizeToFitWidth(
          doc,
          roleText,
          badgeLabelFontSize,
          labelWidth
        )
        const barHeight = 0.47
        const capHeightIn = (resolvedLabelFontSize / 72) * HELVETICA_CAP_HEIGHT_RATIO
        const labelBaselineY = bottomBarY + (barHeight + capHeightIn) / 2
        doc.text(roleText, x + badgeWidth / 2, labelBaselineY, { align: 'center' })
      }

      let x = startX,
        y = startY
      subjects.forEach((subject, index) => {
        drawBadge(subject, qrCodes[index], x, y)
        x += badgeWidth + spacingX
        if ((index + 1) % badgePerRow === 0) {
          x = startX
          y += badgeHeight + spacingY
        }
        if ((index + 1) % (badgePerRow * badgePerColumn) === 0 && index + 1 !== subjects.length) {
          doc.addPage()
          x = startX
          y = startY
        }
      })

      if (submissionType === 'download') {
        doc.save(`${type}s_badges_${orientation}.pdf`)
      } else if (submissionType === 'print') {
        const base64String = doc.output('datauristring').split(',')[1]
        printBase64Pdf(base64String)
      }
    } catch (err) {
      console.error(err.message)
      toast.error('Badge printing failed. Try again.')
    } finally {
      setSelectedRows([])
    }
  }

  function printBase64Pdf(base64String) {
    try {
      const binaryData = Uint8Array.from(atob(base64String), (c) => c.charCodeAt(0))
      const blob = new Blob([binaryData], { type: 'application/pdf' })
      const blobUrl = URL.createObjectURL(blob)
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = blobUrl
      document.body.appendChild(iframe)
      iframe.onload = function () {
        iframe.contentWindow.focus()
        iframe.contentWindow.print()
      }
    } catch (error) {
      console.error('Error printing PDF:', error)
    }
  }

  // Table config
  const columns = [
    'Counter #',
    'Full Name',
    'Email',
    'Credential',
    'Position',
    'Organization',
    'City',
    'State',
    'Country',
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    }
  ]

  const tableData = filteredSubjects.map((obj) => [
    obj?.counter ?? 'N/A',
    `${obj?.lastName ?? ''}, ${obj?.firstName ?? ''}`.trim() || 'N/A',
    obj?.email || 'N/A',
    obj?.credential || 'N/A',
    obj?.position || 'N/A',
    obj?.organization || 'N/A',
    obj?.city || 'N/A',
    obj?.state || 'N/A',
    obj?.country || 'N/A',
    <Box key={obj.id} sx={{ display: 'flex', alignItems: 'center' }}>
      <IconButton size="small" title="Print Badge" onClick={() => openPdfConfigModal([obj])}>
        <BadgeIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={() => openEditModal(obj.id)}>
        <Edit fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={() => openDeleteModal(obj.id)}>
        <Delete fontSize="small" />
      </IconButton>
    </Box>
  ])

  const tableOptions = {
    ...getTableOptions(),
    responsive: 'standard',
    pagination: true,
    rowsPerPage,
    rowsPerPageOptions: [10, 25, 50, 100],
    page,
    onChangePage: (p) => setPage(p),
    onChangeRowsPerPage: (rpp) => setRowsPerPage(rpp),
    selectableRows: 'multiple',
    selectableRowsHeader: true,
    selectableRowsHideCheckboxes: false,
    onRowSelectionChange: handleRowSelectionChange,
    rowsSelected: selectedRows.map((dataIndex) =>
      filteredSubjects.findIndex((_, idx) => idx === dataIndex)
    ),
    customToolbarSelect: (selectedRowsObj) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '8px 12px' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            const selected = selectedRowsObj.data.map((d) => filteredSubjects[d.dataIndex])
            openPdfConfigModal(selected)
          }}>
          Print Badges
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            const selected = selectedRowsObj.data.map((d) => filteredSubjects[d.dataIndex])
            downloadBadgesInCSVV1(typeFilter, selected)
          }}>
          Export Badge Data
        </Button>
        <Button
          variant="contained"
          style={{ backgroundColor: '#d32f2f', color: '#fff' }}
          onClick={() => setBulkDeleteModalOpen(true)}>
          Delete Selected ({selectedRowsObj.data.length})
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <ViewHeader title="Badges" />
        <Button
          color="primary"
          size="small"
          startIcon={<Settings fontSize="small" />}
          onClick={() => history.push(`${url}/configuration`)}
          style={{ textDecoration: 'underline', textTransform: 'none', fontWeight: 500 }}>
          Badge Configurations
        </Button>
      </Box>

      {/* Tabs + action buttons row */}
      <Box className={classes.tabRow}>
        <CustomTabs
          tabs={badgesSubjectsApplication}
          activeTab={typeFilter}
          valueKey="label"
          counts={Object.fromEntries(
            badgesSubjectsApplication.map((type) => [
              type,
              subjects.filter((s) => s.type === type).length
            ])
          )}
          onChange={(val) => {
            setTypeFilter(val)
            setSelectedRows([])
            setPage(0)
          }}
        />

        <Box className={classes.buttonGroupBox}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            endIcon={<KeyboardArrowRight />}
            onClick={() => setCsvModalOpen(true)}>
            Import via CSV
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            endIcon={<KeyboardArrowRight />}
            onClick={openAddModal}>
            Create New
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <Box mt={4}>
        {loading ? (
          <DataLoading />
        ) : filteredSubjects.length ? (
          <Paper variant="outlined" style={{ marginTop: '16px' }}>
            <BasicTable data={tableData} columns={columns} options={tableOptions} />
          </Paper>
        ) : (
          <ResourceNotFound
            message={`No ${typeFilter} badge subjects yet. Add one manually or import via CSV.`}
          />
        )}
      </Box>

      {/* Add / Edit Dialog */}
      <AddEditBadgeSubjectModal
        open={formModalOpen}
        onClose={closeFormModal}
        subjectId={editingSubjectId}
        onSaveAndPrint={async (subjects, { pageSize, orientation, bannerLabel }) => {
          setIsPrinting(true)
          try {
            await downloadBadgesInPdfFile(
              subjects,
              first(subjects).type,
              pageSize,
              orientation,
              'print',
              bannerLabel
            )
          } catch {
            toast.error('PDF generation failed. Try again.')
          } finally {
            setIsPrinting(false)
          }
        }}
        onSaveAndDownload={async (subjects, { pageSize, orientation, bannerLabel }) => {
          setPrintingBadges(true)
          try {
            await downloadBadgesInPdfFile(
              subjects,
              first(subjects).type,
              pageSize,
              orientation,
              'download',
              bannerLabel
            )
          } catch {
            toast.error('Could not generate PDF. Try again.')
          } finally {
            setPrintingBadges(false)
          }
        }}
      />

      {/* Delete Confirmation */}
      <ConfirmModal
        open={deleteModalOpen}
        closeModal={closeDeleteModal}
        dialogTitle="Delete Badge Subject"
        dialogText="Are you sure you want to delete this badge subject? This action cannot be undone."
        handleConfirm={handleDelete}
        inProgress={deleting}
      />

      {/* Bulk Delete Confirmation */}
      <ConfirmModal
        open={bulkDeleteModalOpen}
        closeModal={() => setBulkDeleteModalOpen(false)}
        dialogTitle="Delete Selected Badge Subjects"
        dialogText={`Are you sure you want to delete ${selectedRows.length} badge subject(s)? This action cannot be undone.`}
        handleConfirm={handleBulkDelete}
        inProgress={bulkDeleting}
      />

      {/* CSV Import Modal */}
      <ImportBadgesCSVModal
        open={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        conferenceId={conference?.id}
      />

      {/* PDF Print Config Modal */}
      <PrintBadgesModal
        open={pdfConfigModal}
        onClose={closePdfConfigModal}
        subjects={subjectsToBePrinted}
        loading={printingBadges || isPrinting}
        onPrint={handlePrintSubmit}
        onDownload={handleDownloadSubmit}
      />
    </Box>
  )
}

export default BadgesV1Index
